import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

// Credit Management API hooks
export const usePlanCreditConfigs = () => {
  return useQuery({
    queryKey: ["/api/admin/credits/plan-configs"],
    queryFn: async () => {
      const response = await apiRequest("/api/admin/credits/plan-configs", "GET");
      return response.json();
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useUpdatePlanCreditConfig = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ planType, config }: { planType: string; config: any }) =>
      apiRequest(`/api/admin/credits/plan-configs/${planType}`, "PUT", config),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/credits/plan-configs"] });
      toast({
        title: "Plan Configuration Updated",
        description: `Successfully updated ${variables.planType} plan configuration.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Update Failed",
        description: error.message || "Failed to update plan configuration.",
        variant: "destructive",
      });
    },
  });
};

export const useBulkGrantCredits = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ userIds, amount, reason, planCode }: { userIds: number[]; amount: number; reason: string; planCode?: string }) =>
      apiRequest("/api/admin/credits/bulk-grant", "POST", { userIds, amount, reason, planCode }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/analytics"] });
      toast({
        title: "Credits Granted",
        description: `Successfully granted ${variables.amount} credits to ${variables.userIds.length} users.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Grant Failed",
        description: error.message || "Failed to grant credits.",
        variant: "destructive",
      });
    },
  });
};

export const useBulkDeductCredits = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ userIds, amount, reason, planCode }: { userIds: number[]; amount: number; reason: string; planCode?: string }) =>
      apiRequest("/api/admin/credits/bulk-deduct", "POST", { userIds, amount, reason, planCode }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/analytics"] });
      toast({
        title: "Credits Deducted",
        description: `Successfully deducted ${variables.amount} credits from ${variables.userIds.length} users.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Deduction Failed",
        description: error.message || "Failed to deduct credits.",
        variant: "destructive",
      });
    },
  });
};

// Credit-related types
export interface PlanCreditConfig {
  id: number;
  planType: string;
  monthlyCredits: number;
  addonCreditPrice: number;
  rolloverEnabled: boolean;
  rolloverLimit: number;
  bonusMultiplier: number;
  isActive: boolean;
}

export interface BulkCreditOperation {
  userIds: number[];
  amount: number;
  reason: string;
  planCode?: string;
}

export const useUpdateUserPlan = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ userId, planCode }: { userId: number; planCode: string }) =>
      apiRequest(`/api/admin/users/${userId}/plan`, "PUT", { planCode }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/analytics"] });
      toast({
        title: "User Plan Updated",
        description: `Successfully updated user plan to ${variables.planCode}.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Update Failed",
        description: error.message || "Failed to update user plan.",
        variant: "destructive",
      });
    },
  });
};

// Task Credit Configuration hooks
export const useTaskCreditConfigs = () => {
  return useQuery({
    queryKey: ["/api/admin/credits/task-configs"],
    queryFn: async () => {
      const response = await apiRequest("/api/admin/credits/task-configs", "GET");
      return response.json();
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useUpdateTaskCreditConfig = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ taskType, config }: { taskType: string; config: any }) =>
      apiRequest(`/api/admin/credits/task-configs/${taskType}`, "PUT", config),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/credits/task-configs"] });
      toast({
        title: "Task Configuration Updated",
        description: `Successfully updated ${variables.taskType} task configuration.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Update Failed",
        description: error.message || "Failed to update task configuration.",
        variant: "destructive",
      });
    },
  });
};

export const useCreateTaskCreditConfig = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (config: any) =>
      apiRequest("/api/admin/credits/task-configs", "POST", config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/credits/task-configs"] });
      toast({
        title: "Task Configuration Created",
        description: "Successfully created new task configuration.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Creation Failed",
        description: error.message || "Failed to create task configuration.",
        variant: "destructive",
      });
    },
  });
};